import { ComponentRef, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { AppService } from '@app/app.service';
import { enumHtsChatScope } from '@data-layer/enums/common';
import { HtsChatConversationModel, HtsChatEntryLineModel, HtsChatEntryModel, HtsChatRemovedLineModel } from '@data-layer/models/common';
import { HtsChatRepository } from '@data-layer/repositories';
import { HtsAssistantDrawerComponent } from '../components/hts-assistant-drawer/hts-assistant-drawer.component';
import { enumHtsAssistantMode } from '../enums';
import { HtsLineStatusHelper } from '../helpers/hts-line-status.helper';
import { IHtsAssistantLineRef } from '../interfaces';

export interface IHtsAssistantHistoryConfig {
  entrySummaryId: number;
  /** Picks the read endpoint: importer, broker or admin management. */
  scope: enumHtsChatScope;
  /** Admin, Broker Review (#13548): open on the final conversation only, with the "watch the do-over" toggle. */
  isDoOverToggle: boolean;
}

/**
 * "Classification assistant history" (#13385): every conversation of the entry, read-only, one line at a time with
 * Back / Next. One entry-level GET per opening; the drawer gets the conversations, nothing is fetched per line.
 */
@Injectable({ providedIn: 'root' })
export class HtsAssistantHistoryService {
  private ref: ComponentRef<HtsAssistantDrawerComponent> | null = null;

  constructor(private appService: AppService, private htsChatRepository: HtsChatRepository) {}

  public open(config: IHtsAssistantHistoryConfig): void {
    this.htsChatRepository
      .getEntryHistory(config.entrySummaryId, config.scope)
      .pipe(take(1))
      .subscribe(
        (history: HtsChatEntryModel) => this.show(config, history),
        () => this.show(config, null),
      );
  }

  public close(): void {
    const ref: ComponentRef<HtsAssistantDrawerComponent> | null = this.ref;
    this.ref = null;
    if (ref && this.appService.componentSidebarAsWindowRef === ref) {
      this.appService.closeSidebarAsWindow();
      this.appService.isXlg$.next(false);
    }
  }

  private show(config: IHtsAssistantHistoryConfig, history: HtsChatEntryModel | null): void {
    // the entry's lines first, then the lines deleted after they had conversations ("Removed lines", the broker's Shell
    // summary carries the same group)
    const historyLines: HtsChatEntryLineModel[] = history ? history.lines || [] : [];
    const removedLines: HtsChatRemovedLineModel[] = history ? history.removedLines || [] : [];
    const lines: IHtsAssistantLineRef[] = [
      ...HtsLineStatusHelper.fromHistory(historyLines),
      ...HtsLineStatusHelper.fromRemoved(removedLines, historyLines.length),
    ];
    const conversationsByLine: { [lineId: number]: HtsChatConversationModel[] } = {};
    const finalConversationIdByLine: { [lineId: number]: number | null } = {};
    historyLines.forEach((line: HtsChatEntryLineModel) => {
      conversationsByLine[line.lineId] = line.conversations || [];
      finalConversationIdByLine[line.lineId] = config.isDoOverToggle ? HtsLineStatusHelper.finalConversationId(line) : null;
    });
    removedLines.forEach((line: HtsChatRemovedLineModel) => {
      conversationsByLine[line.lineId] = line.conversations || [];
      // a removed line carries no verification: its latest conversation is the final one (#13548)
      finalConversationIdByLine[line.lineId] = config.isDoOverToggle ? HtsLineStatusHelper.latestConversationId(line.conversations) : null;
    });

    this.close();
    this.appService.isXlg$.next(true);
    this.ref = this.appService.openSidebarAsWindow(HtsAssistantDrawerComponent, {
      sidebarHeaderText: '',
      isMd: true,
      isFillMobile: true,
    });

    const destroyed$: Subject<void> = new Subject<void>();
    this.ref.onDestroy(() => {
      destroyed$.next();
      destroyed$.complete();
    });

    const drawer: HtsAssistantDrawerComponent = this.ref.instance;
    const focus: IHtsAssistantLineRef | null = lines[0] || null;
    drawer.mode = enumHtsAssistantMode.Readonly;
    drawer.scope = config.scope;
    drawer.isDoOverToggle = config.isDoOverToggle;
    drawer.isHistoryFailed = history === null;
    drawer.lines = lines;
    drawer.conversationsByLine = conversationsByLine;
    drawer.finalConversationIdByLine = finalConversationIdByLine;
    drawer.lineId = focus ? focus.lineId : null;
    drawer.invoiceId = focus ? focus.invoiceId : null;
    drawer.selectedLineId = focus ? focus.lineId : null;
    drawer.lineDescription = focus ? focus.description : null;
    drawer.currentHts = focus ? focus.htsNumber : null;
    drawer.verificationSource = focus ? focus.verificationSource : null;
    drawer.verificationMatchedText = focus ? focus.matchedText : null;

    drawer.close.pipe(takeUntil(destroyed$)).subscribe(() => this.close());
    drawer.lineSelected.pipe(takeUntil(destroyed$)).subscribe((lineId: number) => {
      const line: IHtsAssistantLineRef | undefined = lines.find((l: IHtsAssistantLineRef) => l.lineId === lineId);
      if (line) {
        drawer.selectLine(line);
      }
    });
  }
}
